import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, CheckCircle2, ClipboardCheck, Calendar, AlertTriangle, Clock, ChevronRight, Archive } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { Badge } from '../../components/common/Badge';
import { useOKRs } from '../../hooks/useOKRs';
import { useOrgStats } from '../../hooks/useOrgStats';
import { usePermissions } from '../../hooks/usePermissions';
import { useUrlState } from '../../hooks/useUrlState';
import { useOpenWithReturn } from '../../hooks/useReturnNavigation';
import { PeriodSelector } from '../../components/performance/PeriodSelector';
import { PeriodDateSelector, getDefaultPeriod, type SelectedPeriod } from '../../components/performance/PeriodDateSelector';
import { OKR } from '../../types/okr';
import type { PeriodType } from '../../types';

import { keyResultProgressPercent } from '../../utils/progress';

// Progress Ring Component
const ProgressRing: React.FC<{ progress: number; size?: number; status?: string }> = ({
  progress,
  size = 72,
  status = 'default'
}) => {
  const strokeWidth = 6;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;

  const strokeColor = status === 'on_track' || progress >= 70
    ? 'text-success-500'
    : status === 'at_risk' || progress >= 40
    ? 'text-warning-500'
    : 'text-primary-500';

  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg
        className="progress-ring"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
      >
        <circle
          className="text-stone-100"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="none"
          cx={size / 2}
          cy={size / 2}
          r={radius}
        />
        <circle
          className={strokeColor}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          fill="none"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          style={{
            strokeDasharray: circumference,
            strokeDashoffset: offset,
          }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
          {progress}%
        </span>
      </div>
    </div>
  );
};

// OKR Card Component
const OKRCard: React.FC<{ okr: OKR; onClick: () => void }> = ({ okr, onClick }) => {
  const borderColor = okr.status === 'on_track'
    ? 'border-l-success-500'
    : okr.status === 'at_risk'
    ? 'border-l-warning-500'
    : okr.status === 'off_track'
    ? 'border-l-danger-500'
    : 'border-l-stone-300';

  return (
    <div
      onClick={onClick}
      className={`card p-5 border-l-4 ${borderColor} cursor-pointer transition-all duration-200 hover:shadow-soft-md hover:-translate-y-0.5`}
    >
      <div className="flex gap-4">
        <div className="flex-1 min-w-0">
          {/* Badges */}
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-primary-50 text-primary-600">
              {okr.type}
            </span>
            <Badge variant={
              okr.status === 'on_track' ? 'success' :
              okr.status === 'at_risk' ? 'warning' :
              okr.status === 'off_track' ? 'danger' : 'secondary'
            }>
              {okr.status.replace('_', ' ')}
            </Badge>
          </div>

          {/* Title & Description */}
          <h4 className="font-semibold text-stone-900 mb-1 line-clamp-2">{okr.title}</h4>
          {okr.description && (
            <p className="text-sm text-stone-500 line-clamp-2 mb-3">{okr.description}</p>
          )}

          {/* Key Results Preview */}
          <div className="border-t border-stone-100 pt-3 space-y-2">
            {okr.keyResults.slice(0, 3).map((kr) => {
              const krProgress = keyResultProgressPercent(kr);
              return (
                <div key={kr.id} className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    krProgress === 100 ? 'bg-success-500' :
                    krProgress > 0 ? 'bg-warning-500' : 'bg-stone-300'
                  }`} />
                  <span className="text-xs text-stone-600 truncate flex-1">{kr.title}</span>
                  <span className="text-xs font-medium text-stone-500">{krProgress}%</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Progress Ring */}
        <ProgressRing progress={okr.progress || 0} status={okr.status} />
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | number;
  change?: string;
  onClick?: () => void;
}> = ({ icon, label, value, change, onClick }) => (
  <div
    onClick={onClick}
    className={`card p-5 ${onClick ? 'cursor-pointer hover:shadow-soft-md transition-all' : ''}`}
  >
    <div className="flex items-center gap-2 text-stone-500 text-sm mb-2">
      {icon}
      {label}
    </div>
    <div className="text-3xl font-semibold text-stone-900" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
      {value}
    </div>
    {change && (
      <div className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-success-600 bg-success-50 px-2 py-0.5 rounded-full">
        {change}
      </div>
    )}
  </div>
);

// Attention Card Component
const AttentionCard: React.FC<{
  type: 'urgent' | 'warning' | 'info';
  title: string;
  items: Array<{ text: string; action: string; onClick: () => void }>;
}> = ({ type, title, items }) => {
  const config = {
    urgent: {
      bg: 'bg-gradient-to-br from-danger-50 to-white',
      border: 'border-danger-200',
      iconBg: 'bg-danger-500',
      icon: <AlertTriangle className="w-4 h-4" />
    },
    warning: {
      bg: 'bg-gradient-to-br from-warning-50 to-white',
      border: 'border-warning-200',
      iconBg: 'bg-warning-500',
      icon: <Clock className="w-4 h-4" />
    },
    info: {
      bg: 'bg-gradient-to-br from-success-50 to-white',
      border: 'border-success-200',
      iconBg: 'bg-success-500',
      icon: <CheckCircle2 className="w-4 h-4" />
    }
  };

  const { bg, border, iconBg, icon } = config[type];

  return (
    <div className={`rounded-xl p-4 ${bg} border ${border}`}>
      <div className="flex items-center gap-2.5 mb-4">
        <div className={`w-8 h-8 rounded-lg ${iconBg} text-white flex items-center justify-center`}>
          {icon}
        </div>
        <span className="font-semibold text-stone-900">{title}</span>
      </div>
      <div className="space-y-2">
        {items.map((item, idx) => (
          <div key={idx} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0">
            <span className="text-sm text-stone-600">{item.text}</span>
            <button
              onClick={item.onClick}
              className="text-xs font-medium text-primary-500 hover:text-primary-600 flex items-center gap-1"
            >
              {item.action} <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const DASHBOARD_PERIOD_TYPES = ['monthly', 'quarterly', 'half_yearly', 'yearly'] as const;
const DASHBOARD_STATUS_FILTERS = ['all', 'on_track', 'at_risk'] as const;
const isValidDate = (value: string) => !!value && !Number.isNaN(Date.parse(value));
const periodFromView = (start: string, end: string, periodType: PeriodType): SelectedPeriod =>
  isValidDate(start) && isValidDate(end) ? { startDate: start, endDate: end } : getDefaultPeriod(periodType);

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canCreateOKR } = usePermissions();

  const openWithReturn = useOpenWithReturn();

  // Period and status filter live in the URL (hooks/useUrlState.ts): in component state they
  // reset whenever the user opened an OKR and came back.
  const [view, setView] = useUrlState(
    { period: 'quarterly', start: '', end: '', status: 'all' },
    { period: DASHBOARD_PERIOD_TYPES, status: DASHBOARD_STATUS_FILTERS }
  );
  const periodType = view.period as PeriodType;
  const selectedPeriod: SelectedPeriod = useMemo(
    () => periodFromView(view.start, view.end, periodType),
    [view.start, view.end, periodType]
  );
  const setSelectedPeriod = useCallback(
    (period: SelectedPeriod) => setView({ start: period.startDate, end: period.endDate }),
    [setView]
  );

  const handlePeriodTypeChange = useCallback(
    (newPeriodType: PeriodType) => {
      const next = getDefaultPeriod(newPeriodType);
      setView({ period: newPeriodType, start: next.startDate, end: next.endDate });
    },
    [setView]
  );

  // Organization-level stats for the top cards, filtered by selected period
  const { data: orgStats, isLoading: orgStatsLoading } = useOrgStats({
    startDate: selectedPeriod.startDate,
    endDate: selectedPeriod.endDate,
  });

  // Human-readable label for the selected period (e.g. "Q2 2026")
  const periodLabel = useMemo(() => {
    const start = new Date(selectedPeriod.startDate);
    const year = start.getFullYear();
    switch (periodType) {
      case 'quarterly': {
        const q = Math.floor(start.getMonth() / 3) + 1;
        return `Q${q} ${year}`;
      }
      case 'monthly':
        return start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      case 'half_yearly':
        return `${start.getMonth() < 6 ? 'H1' : 'H2'} ${year}`;
      case 'yearly':
        return `${year}`;
      default:
        return '';
    }
  }, [selectedPeriod, periodType]);

  // Step backward one period (e.g. previous quarter)
  // Functional, from the newest URL value: fast clicks must each step back one period.
  const goToPreviousPeriod = useCallback(() => setView((cur) => {
    const periodType = cur.period as PeriodType;
    const start = new Date(periodFromView(cur.start, cur.end, periodType).startDate);
    let newStart: Date;
    let newEnd: Date;
    switch (periodType) {
      case 'monthly':
        newStart = new Date(start.getFullYear(), start.getMonth() - 1, 1);
        newEnd = new Date(start.getFullYear(), start.getMonth(), 0, 23, 59, 59, 999);
        break;
      case 'quarterly':
        newStart = new Date(start.getFullYear(), start.getMonth() - 3, 1);
        newEnd = new Date(start.getFullYear(), start.getMonth(), 0, 23, 59, 59, 999);
        break;
      case 'half_yearly':
        newStart = new Date(start.getFullYear(), start.getMonth() - 6, 1);
        newEnd = new Date(start.getFullYear(), start.getMonth(), 0, 23, 59, 59, 999);
        break;
      case 'yearly':
        newStart = new Date(start.getFullYear() - 1, 0, 1);
        newEnd = new Date(start.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
        break;
      default:
        return {};
    }
    return { start: newStart.toISOString(), end: newEnd.toISOString() };
  }), [setView]);

  // My OKRs status filter
  const okrStatusFilter = view.status as (typeof DASHBOARD_STATUS_FILTERS)[number];
  const setOkrStatusFilter = (status: (typeof DASHBOARD_STATUS_FILTERS)[number]) => setView({ status });

  // My OKRs section: Account admins see top OKRs, others see only their own.
  // Scoped to the same period as the org stats so users don't see stale prior-quarter OKRs.
  const isAccountAdmin = user?.role === 'account_admin';
  const myOkrsFilters = {
    ...(isAccountAdmin ? {} : { ownerIds: user?.id ? [user.id] : [] }),
    limit: 4,
    startDate: selectedPeriod.startDate,
    endDate: selectedPeriod.endDate,
    ...(okrStatusFilter !== 'all' ? { statuses: [okrStatusFilter] } : {}),
  };
  const { data: okrsData, isLoading: okrsLoading } = useOKRs(myOkrsFilters);

  const [greeting, setGreeting] = useState('');

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting('Good morning');
    else if (hour < 18) setGreeting('Good afternoon');
    else setGreeting('Good evening');
  }, []);

  if (!user) {
    return (
      <div className="flex items-center justify-center h-screen bg-cream">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // My OKRs from filtered query — additionally filter client-side to OKRs whose
  // explicit date range overlaps the selected period. The backend treats OKRs with
  // null start/end dates as always-matching, but for the Dashboard we want strict
  // period scoping so this section stays consistent with the org stats cards.
  const periodStartMs = new Date(selectedPeriod.startDate).getTime();
  const periodEndMs = new Date(selectedPeriod.endDate).getTime();
  const myOkrs = (okrsData?.okrs || []).filter((okr) => {
    if (!okr.startDate || !okr.endDate) return false;
    const okrStart = new Date(okr.startDate).getTime();
    const okrEnd = new Date(okr.endDate).getTime();
    return okrStart <= periodEndMs && okrEnd >= periodStartMs;
  });

  // Count on-track KRs for display
  const onTrackKRs = orgStats
    ? Math.round((orgStats.avgProgress / 100) * orgStats.totalKeyResults)
    : 0;

  // Filter OKRs needing attention
  const atRiskOkrs = myOkrs.filter(okr => okr.status === 'at_risk' || okr.status === 'off_track');

  return (
    <div className="min-h-screen bg-cream">
      <div className="px-4 sm:px-6 lg:px-10 py-8">
        {/* Welcome Section */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-3xl font-medium text-stone-900 mb-1" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
              {greeting}, <span className="text-primary-500">{user.firstName}</span>
            </h2>
            <p className="text-stone-500">
              {orgStats && orgStats.totalActiveOKRs > 0
                ? `Your organization has ${orgStats.totalActiveOKRs} OKRs in ${periodLabel}.`
                : `No OKRs defined for ${periodLabel} yet.`}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <PeriodSelector
              value={periodType}
              onChange={handlePeriodTypeChange}
              disabled={orgStatsLoading}
            />
            <PeriodDateSelector
              periodType={periodType}
              selectedPeriod={selectedPeriod}
              onChange={setSelectedPeriod}
              disabled={orgStatsLoading}
            />
          </div>
        </div>

        {/* Organization Overview Section */}
        <section className="mb-8">
          <h3 className="text-sm font-medium text-stone-500 uppercase tracking-wider mb-3">
            Organization Overview
          </h3>
          <div className={`grid grid-cols-2 gap-4 ${periodType === 'yearly' ? 'lg:grid-cols-5' : 'lg:grid-cols-4'}`}>
            <StatCard
              icon={<Target className="w-4 h-4" />}
              label="Total OKRs"
              value={orgStatsLoading ? '-' : (orgStats?.totalActiveOKRs ?? 0)}
              change="In selected period"
              onClick={() => navigate('/okrs')}
            />
            <StatCard
              icon={<CheckCircle2 className="w-4 h-4" />}
              label="Avg Progress"
              value={orgStatsLoading ? '-' : `${orgStats?.avgProgress ?? 0}%`}
              change={orgStats && orgStats.avgProgress >= 50 ? 'On track' : 'Needs attention'}
            />
            <StatCard
              icon={<ClipboardCheck className="w-4 h-4" />}
              label="Total Key Results"
              value={orgStatsLoading ? '-' : (orgStats?.totalKeyResults ?? 0)}
              change={onTrackKRs > 0 ? `~${onTrackKRs} on track` : undefined}
            />
            <StatCard
              icon={<Calendar className="w-4 h-4" />}
              label="Check-ins Due"
              value={orgStatsLoading ? '-' : (orgStats?.checkInsDue ?? 0)}
              change="Organization-wide"
            />
            {periodType === 'yearly' && (
              <StatCard
                icon={<Archive className="w-4 h-4" />}
                label="Closed OKRs"
                value={orgStatsLoading ? '-' : (orgStats?.closedOKRs ?? 0)}
                change="Closed this year"
                onClick={() => navigate('/okrs?statuses=closed')}
              />
            )}
          </div>
        </section>

        {/* My OKRs Section */}
        <section className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-medium text-stone-900" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
              {isAccountAdmin ? 'Top OKRs' : 'My OKRs'}
            </h3>
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-1 bg-stone-100 p-1 rounded-full">
                {([['all', 'All'], ['on_track', 'On Track'], ['at_risk', 'At Risk']] as const).map(([value, label]) => (
                  <button
                    key={value}
                    onClick={() => setOkrStatusFilter(value)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-full transition-all ${
                      okrStatusFilter === value
                        ? 'text-stone-900 bg-white shadow-soft-sm'
                        : 'text-stone-500 hover:text-stone-900'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <Button variant="ghost" size="sm" onClick={() => navigate('/okrs')}>
                View all
              </Button>
            </div>
          </div>

          {okrsLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : myOkrs.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {myOkrs.slice(0, 4).map((okr) => (
                <OKRCard
                  key={okr.id}
                  okr={okr}
                  onClick={() => openWithReturn(`/okrs/${okr.id}`)}
                />
              ))}
            </div>
          ) : (
            <Card className="p-8 text-center" data-testid="dashboard-okrs-empty">
              <Target className="w-12 h-12 mx-auto text-stone-300 mb-4" />
              <h3 className="font-semibold text-stone-900 mb-2">No OKRs for {periodLabel}</h3>
              <p className="text-stone-500 mb-4">No OKRs have been defined for this period yet.</p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                {canCreateOKR() && (
                  <Button variant="primary" onClick={() => openWithReturn('/okrs/new')}>
                    Create OKR
                  </Button>
                )}
                <Button variant="ghost" onClick={goToPreviousPeriod}>
                  View previous period
                </Button>
              </div>
            </Card>
          )}
        </section>

        {/* Needs Attention Section */}
        {(atRiskOkrs.length > 0) && (
          <section>
            <h3 className="text-xl font-medium text-stone-900 mb-4" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
              Needs Your Attention
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {atRiskOkrs.length > 0 && (
                <AttentionCard
                  type="urgent"
                  title="OKRs Falling Behind"
                  items={atRiskOkrs.slice(0, 2).map(okr => ({
                    text: okr.title,
                    action: 'Update',
                    onClick: () => openWithReturn(`/okrs/${okr.id}`)
                  }))}
                />
              )}
              <AttentionCard
                type="warning"
                title="Check-ins Due"
                items={[
                  { text: 'Weekly check-in reminder', action: 'Check in', onClick: () => navigate('/check-ins') }
                ]}
              />
              <AttentionCard
                type="info"
                title="Pending Approvals"
                items={[
                  { text: 'Review team submissions', action: 'Review', onClick: () => navigate('/approvals/pending') }
                ]}
              />
            </div>
          </section>
        )}
      </div>
    </div>
  );
};
